import { Modal, Form, Input, Button, message, List, Typography, InputRef } from 'antd';
import { useState, useEffect, useRef } from 'react';
import { changePassword, IChangePasswordRequest } from 'src/api/auth';
import { useNavigate } from 'react-router-dom';
import { useAppDispatch } from 'src/redux/hook';
import { reset } from 'src/redux/slice/globalSlice';
import {
  TIMEOUT,
  ERROR_MESSAGES,
  PASSWORD_REQUIREMENTS,
  APP_CODE,
  SUCCESS_CODE,
  SUCCESS_MESSAGE_CHANGE_PASSWORD,
} from 'src/shared/constants';
import { z } from 'zod';
import { createPasswordSchema } from 'src/hooks/usePasswordValidation';
import { parseJwt, clearLocalStorage } from 'src/utils';
import { formatExpireTime } from 'src/pages/forgot-password/helper';
const { Text } = Typography;

interface IModalChangePasswordProps {
  visible: boolean;
  onClose: () => void;
}

export const ModalChangePassword = ({ visible, onClose }: IModalChangePasswordProps) => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const currentPasswordRef = useRef<InputRef>(null);

  useEffect(() => {
    if (visible) {
      form.resetFields();
      focusCurrentPasswordField();
    }
  }, [visible, form]);

  const focusCurrentPasswordField = () => {
    setTimeout(() => {
      currentPasswordRef.current?.focus();
    }, TIMEOUT.FOCUS_DELAY);
  };

  const handleLogout = () => {
    dispatch(reset());
    clearLocalStorage();
    navigate('/login');
  };
  const validatePassword = async (_: any, value: string): Promise<void> => {
    if (!value) {
      throw new Error(ERROR_MESSAGES.REQUIRED_NEW_PASSWORD);
    }

    const currentPassword = form.getFieldValue('currentPassword');
    const passwordSchema = createPasswordSchema(currentPassword);

    try {
      passwordSchema.parse(value);
    } catch (error) {
      if (error instanceof z.ZodError) {
        throw new Error(error.issues[0].message);
      }
      throw error;
    }
  };

  const validateConfirmPassword = async (_: any, value: string): Promise<void> => {
    if (!value) {
      throw new Error(ERROR_MESSAGES.REQUIRED_CONFIRM);
    }
    const confirmPasswordSchema = z
      .string()
      .min(1, ERROR_MESSAGES.REQUIRED_CONFIRM)
      .transform((val) => val.trim())
      .refine(
        (val) => {
          const newPassword = form.getFieldValue('newPassword');
          return val === newPassword?.trim();
        },
        {
          message: ERROR_MESSAGES.CONFIRM_NOT_MATCH,
        },
      );

    try {
      confirmPasswordSchema.parse(value);
    } catch (error) {
      if (error instanceof z.ZodError) {
        throw new Error(error.issues[0].message);
      }
      throw error;
    }
  };

  const handleSubmit = async (values: {
    currentPassword: string;
    newPassword: string;
    confirmPassword: string;
  }) => {
    try {
      setLoading(true);

      const token = localStorage.getItem('token');
      if (!token) {
        message.error('Phiên đăng nhập đã hết hạn. Vui lòng đăng nhập lại');
        handleLogout();
        return;
      }

      const decodedToken = parseJwt(token);
      const username = decodedToken?.sub || decodedToken?.username;

      if (!username) {
        message.error('Không thể xác định thông tin người dùng');
        return;
      }

      const requestData: IChangePasswordRequest = {
        username,
        appCode: APP_CODE,
        oldPassword: values.currentPassword.trim(),
        newPassword: values.newPassword.trim(),
      };

      const res = await changePassword(requestData);

      if (res?.code === SUCCESS_CODE) {
        Modal.success({
          title: SUCCESS_MESSAGE_CHANGE_PASSWORD.TITLE,
          content: SUCCESS_MESSAGE_CHANGE_PASSWORD.CHANGE_SUCCESS,
          okText: 'Đóng',
          centered: true,
          onOk: () => {
            form.resetFields();
            onClose();
            handleLogout();
          },
        });
      } else {
        message.error(res?.message || ERROR_MESSAGES.CHANGE_FAILED);
        form.resetFields();
        focusCurrentPasswordField();
      }
    } catch (error: any) {
      const errorCode = error?.response?.data?.code;
      const errorMessage = error?.response?.data?.message;

      if (errorCode === 'E214_AUTH') {
        const formattedTime = formatExpireTime(error?.response?.data?.data?.expireIn);
        message.error(`${errorMessage}. Thời gian mở khóa: ${formattedTime}`, 5);
      } else {
        message.error(errorMessage || ERROR_MESSAGES.CHANGE_FAILED);
      }
      form.resetFields();
      focusCurrentPasswordField();
      setLoading(false);
    }
  };

  const handleCancel = () => {
    if (!loading) {
      form.resetFields();
      onClose();
    }
  };

  const handleModalClose = () => {
    if (!loading) {
      form.resetFields();
      onClose();
    }
  };

  return (
    <Modal
      title='Đổi mật khẩu'
      open={visible}
      onCancel={handleModalClose}
      width={500}
      centered
      closable={!loading}
      maskClosable={!loading}
      footer={null}
      destroyOnClose
    >
      <Form
        form={form}
        layout='vertical'
        onFinish={handleSubmit}
        autoComplete='off'
        className='mt-4'
      >
        <Form.Item
          label='Mật khẩu hiện tại'
          name='currentPassword'
          rules={[
            { required: true, message: ERROR_MESSAGES.REQUIRED_CURRENT_PASSWORD },
            { whitespace: true, message: ERROR_MESSAGES.WHITESPACE_ONLY },
          ]}
          normalize={(value) => value.trimStart()}
        >
          <Input.Password
            ref={currentPasswordRef}
            placeholder='Nhập mật khẩu hiện tại'
            size='large'
            disabled={loading}
            aria-label='Mật khẩu hiện tại'
            autoComplete='current-password'
          />
        </Form.Item>

        <Form.Item
          label='Mật khẩu mới'
          name='newPassword'
          rules={[
            { validator: validatePassword },
            { whitespace: true, message: ERROR_MESSAGES.WHITESPACE_ONLY },
          ]}
          normalize={(value) => value.trimStart()}
          dependencies={['currentPassword']}
        >
          <Input.Password
            placeholder='Nhập mật khẩu mới'
            size='large'
            disabled={loading}
            aria-label='Mật khẩu mới'
            autoComplete='new-password'
          />
        </Form.Item>

        <Form.Item
          label='Xác nhận mật khẩu mới'
          name='confirmPassword'
          rules={[
            { validator: validateConfirmPassword },
            { whitespace: true, message: ERROR_MESSAGES.WHITESPACE_ONLY },
          ]}
          normalize={(value) => value.trimStart()}
          dependencies={['newPassword']}
        >
          <Input.Password
            placeholder='Nhập lại mật khẩu mới'
            size='large'
            disabled={loading}
            aria-label='Xác nhận mật khẩu mới'
            autoComplete='new-password'
          />
        </Form.Item>

        <div className='flex justify-end gap-2 mt-6'>
          <Button onClick={handleCancel} disabled={loading} size='large'>
            Hủy
          </Button>
          <Button type='primary' htmlType='submit' loading={loading} size='large'>
            Xác nhận
          </Button>
        </div>
      </Form>

      <div className='mt-4 p-3  rounded'>
        <Text strong className='block mb-1'>
          Yêu cầu mật khẩu:
        </Text>
        <List
          size='small'
          dataSource={PASSWORD_REQUIREMENTS}
          renderItem={(item: string) => (
            <List.Item className='py-1'>
              <Text type='secondary' className='text-xs'>
                • {item}
              </Text>
            </List.Item>
          )}
        />
      </div>
    </Modal>
  );
};
